/**
 * ConferenciaAdminFields — Inline editable admin fields (NF, Receb, Conferente, Obs).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ConferenciaVerificacaoDetail } from '../../services/conferenciaService';
import { Save } from 'lucide-react';

interface Props {
  conferencia: ConferenciaVerificacaoDetail;
  onSave: (data: {
    notas_fiscais?: string;
    data_recebimento_nf?: string;
    observacoes?: string;
  }) => Promise<void>;
  readOnly: boolean;
  saving: boolean;
}

export const ConferenciaAdminFields: React.FC<Props> = ({ conferencia, onSave, readOnly, saving }) => {
  const [notasFiscais, setNotasFiscais] = useState(conferencia.notas_fiscais || '');
  const [dataRecebNf, setDataRecebNf] = useState(conferencia.data_recebimento_nf || '');
  const [observacoes, setObservacoes] = useState(conferencia.observacoes || '');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setNotasFiscais(conferencia.notas_fiscais || '');
    setDataRecebNf(conferencia.data_recebimento_nf || '');
    setObservacoes(conferencia.observacoes || '');
    setDirty(false);
  }, [conferencia]);

  const handleChange = useCallback((setter: React.Dispatch<React.SetStateAction<string>>) => {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setter(e.target.value);
      setDirty(true);
    };
  }, []);

  const handleSave = async () => {
    // Convert DD/MM/YYYY to YYYY-MM-DD if present
    let nfDate = dataRecebNf;
    if (nfDate && nfDate.includes('/')) {
      const parts = nfDate.split('/');
      if (parts.length === 3) {
        nfDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }

    await onSave({
      notas_fiscais: notasFiscais,
      data_recebimento_nf: nfDate || undefined,
      observacoes,
    });
    setDirty(false);
  };

  const inputClass = "w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-blue-500 focus:border-transparent";

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Dados Administrativos</h4>
        {dirty && !readOnly && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-3 h-3" />
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Notas Fiscais</label>
          <input
            type="text"
            value={notasFiscais}
            onChange={handleChange(setNotasFiscais)}
            disabled={readOnly}
            className={inputClass}
            placeholder="Ex: NF-001, NF-002"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Receb. NF</label>
          <input
            type="date"
            value={dataRecebNf}
            onChange={handleChange(setDataRecebNf)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Conferente</label>
          <input
            type="text"
            value={conferencia.conferente_nome || '\u2014'}
            disabled
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-3">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Observação</label>
        <textarea
          value={observacoes}
          onChange={handleChange(setObservacoes)}
          disabled={readOnly}
          rows={2}
          className={inputClass + ' resize-none'}
          placeholder="Observações gerais..."
        />
      </div>
    </div>
  );
};
